import './warranty-modal.scss';
import {ParameterValidator} from '@mvps-genesis/utilities';
import MicroModal from 'micromodal';
import EventBus from '@mvps-genesis/event-bus';
import Swiper from 'swiper/bundle';

export default class WarrantyProcess {

	constructor (apiUrl, basketUrl) {
		this.validator = new ParameterValidator(this);

		this.apiUrl = this.validator.isString('apiUrl', apiUrl);
		this.basketUrl = this.validator.isString('basketUrl', basketUrl);

		this.editModelTriggers = this.validator.isElementList('editModelTriggers', '.js-warranty-edit-model', {required: false});
		this.clearCartTrigger = this.validator.isElement('clearCartTrigger', '#js-warranty-clear-cart', {required: false});
		this.moveItemsTrigger = this.validator.isElement('moveItemsTrigger', '#js-warranty-mtwl', {required: false});
		this.warrantyListsSliderWrap = this.validator.isElement('warrantyListsSliderWrap', '#js-warranty-slider', {required: false});
		this.warrantyListsSliderBackElems = this.validator.isElementList('warrantyListsSliderBackElems', '.js-edit-modal-back', {required: false});
		this.createNewListTrigger = this.validator.isElement('createNewListTrigger', '#js-create-list', {required: false});
		this.createNewListForm = this.validator.isElement('createNewListForm', '#js-warranty-new-list', {required: false});
		this.wishlistAnchor = this.validator.isElement('wishlistAnchor', '#js-last-saved-lists', {required: false});
		this.createNewListErrorMessagesElem = this.validator.isElement('createNewListErrorMessagesElem', '#js-error-messages', {required: false});

		this.warrantyWishlistSlide2 = this.validator.isElement('warrantyWishlistSlide2', '#js-warranty-edit-screen-2', {required: false});

		this.warrantySearchSlideIndex = 0;
		this.warrantyActionSlideIndex = 1;

		this.bindEvents();

		// Check if `Modal_Warranty_Claim_Open` is set to 1, and it's `BASK`
		if (window.mivaJS.Screen === 'BASK') {
			let params = new URLSearchParams(window.location.search);

			if (params.get('Modal_Warranty_Claim_Open')) {
				this.openWarrantyProcessModal();
			}
		}
	}

	bindEvents () {
		const self = this;

		if (this.editModelTriggers.length) {
			for (const editTrigger of this.editModelTriggers) {
				editTrigger.addEventListener('click', (e) => this.openWarrantyProcessModal(e));
			}
		}

		if (this.clearCartTrigger) {
			this.clearCartTrigger.addEventListener('click', (e) => this.clearCart(e));
		}

		if (this.moveItemsTrigger) {
			this.moveItemsTrigger.addEventListener('click', (e) => this.moveItems(e));
		}

		EventBus.addEventListener('MiniModal:onShow', function (e) {
			if (e.detail.id === 'modal-warranty-edit') {
				self.resetForms();
			}
		});

		if (this.createNewListForm) {
			this.createNewListForm.addEventListener('submit', (e) => this.newListFormSubmit(e));
		}

		if (this.warrantyListsSliderWrap) {
			this.bindSliderEvents();
		}
	}

	bindSliderEvents () {
		this.warrantyListsSlider = new Swiper(this.warrantyListsSliderWrap, {
			allowSlideNext: false,
			allowSlidePrev: false,
			allowTouchMove: false,
			effect: 'fade',
			navigation: false,
			noSwiping: true,
			observeParents: true,
			observer: true,
			preventClicks: false,
			preventClicksPropagation: false,
			slidesPerView: 1,
			spaceBetween: 0,
			speed: 325,
			touchStartPreventDefault: false
		});

		if (this.warrantyListsSliderBackElems) {
			for (const backElem of this.warrantyListsSliderBackElems) {
				backElem.addEventListener('click', () => this.savedListsSlidePrev());
			}
		}

		if (this.createNewListTrigger) {
			this.createNewListTrigger.addEventListener('click', ()=> this.savedListsSlideNext());
		}
	}

	resetForms () {
		if (this.createNewListForm) {
			this.createNewListForm.reset();
		}

		if (this.createNewListErrorMessagesElem) {
			this.createNewListErrorMessagesElem.innerHTML = '';
		}
	}

	openWarrantyProcessModal (e) {
		if (e) {
			e.preventDefault();
		}

		let disableFocus = false;

		if (window.mivaJS.Basket_Count === 0 || window.mivaJS.Warranty_Checkout === 0) {
			this.savedListsSlideTo(this.warrantySearchSlideIndex);
		} else {
			this.savedListsSlideTo(this.warrantyActionSlideIndex);
			disableFocus = true;
		}

		MicroModal.show('modal-warranty-edit', {
			awaitCloseAnimation: true,
			awaitOpenAnimation: true,
			disableFocus: disableFocus,
			disableScroll: true,
			onClose: modal => EventBus.dispatchEvent('MiniModal:onClose', modal),
			onShow: modal => EventBus.dispatchEvent('MiniModal:onShow', modal)
		});
	}

	async clearCart (e) {
		e.preventDefault();

		const response = await this.clearCartContents(this.clearCartTrigger.getAttribute('data-href'));

		if (window.mivaJS.Screen === 'BASK') {
			return true;
		}

		const html = document.createElement('div');
		html.innerHTML = response;

		let payload = {
			data: html,
			doNotOpen: true,
			success: true
		};

		EventBus.dispatchEvent('WarrantyCheckout:clearCart', payload);

		this.savedListsSlideTo(this.warrantySearchSlideIndex);

		return true;
	}

	async clearCartContents (url) {
		this.clearCartTrigger.disabled = true;
		this.clearCartTrigger.setAttribute('data-original-text', this.clearCartTrigger.innerText);
		this.clearCartTrigger.innerText = 'Processing...';

		this.disableButtons(true);

		const response = await fetch(url, {
			method: 'POST'
		});

		if (window.mivaJS.Screen === 'BASK') {
			const newUrl = new URL(window.location.href);
			newUrl.searchParams.append('Modal_Warranty_Claim_Open', 1);

			window.location.href = newUrl;

			return true;
		}

		this.clearCartTrigger.disabled = false;
		this.clearCartTrigger.innerText = this.clearCartTrigger.getAttribute('data-original-text');

		this.disableButtons(false);

		if (!response.ok) {
			throw new Error('Network response was not successful');
		}

		return response.text();
	}

	async moveItems (e) {
		e.preventDefault();

		const data = await this.getSavedLists();

		const html = this.buildListsSlide(data);

		const savedListsElem = this.warrantyWishlistSlide2.querySelector('#js-saved-lists');

		if (this.warrantyListsSlider && this.warrantyWishlistSlide2 && savedListsElem) {
			savedListsElem.innerHTML = html;

			const buttons = savedListsElem.querySelectorAll('.js-warranty-saved-list');

			for (const button of buttons) {
				button.addEventListener('click', () => this.moveItemsToList(button.getAttribute('data-id')));
			}

			this.savedListsSlideNext();
		}

	}

	async getSavedLists () {
		this.moveItemsTrigger.disabled = true;
		this.disableButtons(true);

		const response = await fetch(this.apiUrl, {
			body: JSON.stringify({
				Function: 'GET_SAVED_LISTS'
			}),
			method: 'POST'
		});

		this.moveItemsTrigger.disabled = false;
		this.disableButtons(false);

		if (!response.ok) {
			throw new Error('Network response was not successful');
		}

		return response.json();
	}

	buildListsSlide (data) {
		let html = ``;

		if (data.saved_list_count > 0) {
			html += `<div class="t-warranty-saved-lists u-font-secondary">`;
			for (const list of data.saved_lists) {
				html += `
					<button type="button" class="js-warranty-saved-list u-no-button-styling t-warranty-saved-list u-flex o-layout--align-center o-layout--justify-between" data-id="${list.id}">
						<span class="t-warranty-saved-list--title">${list.title}</span>
						<span class="t-warranty-saved-list--icon u-icon-add-circle u-color-primary"></span>
					</button>
				`;
			}

			html += `</div>`;
		}

		return html;
	}

	savedListsSlideNext () {
		if (!this.warrantyListsSlider) {
			return;
		}

		this.warrantyListsSlider.allowSlideNext = true;
		this.warrantyListsSlider.slideNext();
		this.warrantyListsSlider.allowSlideNext = false;
	}

	savedListsSlidePrev () {
		if (!this.warrantyListsSlider) {
			return;
		}

		this.warrantyListsSlider.allowSlidePrev = true;
		this.warrantyListsSlider.slidePrev();
		this.warrantyListsSlider.allowSlidePrev = false;
	}

	savedListsSlideTo (index) {
		if (!this.warrantyListsSlider) {
			return;
		}

		this.warrantyListsSlider.allowSlideNext = true;
		this.warrantyListsSlider.allowSlidePrev = true;
		this.warrantyListsSlider.slideTo(index);
		this.warrantyListsSlider.allowSlideNext = false;
		this.warrantyListsSlider.allowSlidePrev = false;
	}

	async newListFormSubmit (e) {
		e.preventDefault();

		this.disableButtons(true);

		const data = new FormData(this.createNewListForm);

		const response = await this.submitRequest(this.createNewListForm.action, data);

		const listID = this.getNewListID(response);

		this.disableButtons(false);

		if (listID) {
			return this.moveItemsToList(listID);
		}

		return false;
	}

	getNewListID (response) {
		const html = document.createElement('div');
		html.innerHTML = response;

		const data = html.querySelector('#ajax_atwl_content');
		const wishlistElem = data.querySelector('#js-iwsh-id');
		const errorMessages = data.querySelector('.x-messages--error');

		if (wishlistElem) {
			return wishlistElem.getAttribute('data-id');
		}

		if (errorMessages && this.createNewListErrorMessagesElem) {
			this.createNewListErrorMessagesElem.innerHTML = errorMessages;
		}

		return false;
	}

	updateWishlistLink (response) {
		const html = document.createElement('div');
		html.innerHTML = response;

		const data = html.querySelector('#ajax_atwl_content');
		const wishlistElem = data.querySelector('#js-iwsh-id');

		if (wishlistElem && this.wishlistAnchor) {
			this.wishlistAnchor.setAttribute('href', wishlistElem.getAttribute('data-link'));
			this.wishlistAnchor.innerText = wishlistElem.getAttribute('data-title');
		}
	}

	async submitRequest (url, formdata) {

		const response = await fetch(url, {
			body: formdata,
			method: 'POST'
		});

		if (!response.ok) {
			throw new Error('Network response was not successful');
		}

		return response.text();
	}

	async moveItemsToList (id) {
		this.disableButtons(true);

		const data = new FormData();

		data.append('Action', 'MAWL');
		data.append('WishList_ID', id);

		const response = await this.submitRequest(this.basketUrl, data);

		const html = document.createElement('div');
		html.innerHTML = response;

		let payload = {
			data: html,
			doNotOpen: true,
			success: true
		};

		EventBus.dispatchEvent('WarrantyCheckout:clearCart', payload);

		if (window.mivaJS.Screen === 'BASK') {
			const newUrl = new URL(window.location.href);
			newUrl.searchParams.append('Modal_Warranty_Claim_Open', 1);

			window.location.href = newUrl;

			return;
		}

		this.disableButtons(false);

		this.updateWishlistLink(response);

		this.savedListsSlideTo(this.warrantyListsSlider.slides.length - 1);
	}

	disableButtons (disabled) {
		const buttons = this.warrantyListsSliderWrap.querySelectorAll('button');

		if (buttons) {
			for (const button of buttons) {
				button.disabled = disabled;
			}
		}
	}
}
